/**
 * Utility functions for formatting names and contact info throughout the application
 */

/**
 * Format a phone number as (###) ###-####
 * Handles 10-digit numbers with or without country code
 * @param phone - The phone number to format
 * @returns Formatted phone string or original if not a valid 10-digit number
 */
export function formatPhoneNumber(phone: string | undefined | null): string {
  if (!phone) return '';
  
  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, '');
  
  // Handle 10-digit number
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  
  // Handle 11-digit number (with country code 1)
  if (digits.length === 11 && digits.startsWith('1')) {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  
  // Return original if not a standard format
  return phone;
}

/**
 * Format a legislator's name in "Last Name, First Name" format
 * @param firstName - The legislator's first name
 * @param lastName - The legislator's last name
 * @returns Formatted name string
 */
export function formatLegislatorName(firstName: string, lastName: string): string {
  if (!lastName && !firstName) return '';
  if (!lastName) return firstName;
  if (!firstName) return lastName;
  return `${lastName}, ${firstName}`;
}

/**
 * Format a legislator's name from an object that may have different property naming conventions
 * Handles both snake_case (first_name, last_name) and camelCase (firstName, lastName)
 * @param legislator - Object containing first/last name fields
 * @returns Formatted name string
 */
export function formatLegislatorNameFromObject(legislator: {
  first_name?: string;
  last_name?: string;
  firstName?: string;
  lastName?: string;
  full_name?: string;
  fullName?: string;
}): string {
  const firstName = legislator.firstName || legislator.first_name || '';
  const lastName = legislator.lastName || legislator.last_name || '';
  
  // If we have first and last name, format properly
  if (firstName || lastName) {
    return formatLegislatorName(firstName, lastName);
  }
  
  // Fallback to full_name if no first/last name available
  return legislator.fullName || legislator.full_name || '';
}

/**
 * Universal "Last, First" formatter for any person entity.
 * Alias for formatLegislatorNameFromObject — works with legislators, candidates, or any
 * object that has first/last name fields in either camelCase or snake_case.
 */
export const formatPersonName = formatLegislatorNameFromObject;
